"use client";

import { X } from "lucide-react";
import { useEffect, useId, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils/cn";
import { IconButton } from "./button";

export type DrawerProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  side?: "left" | "right";
  className?: string;
};

export function Drawer({ open, onClose, title, description, children, footer, side = "right", className }: DrawerProps) {
  const titleId = useId();
  const descriptionId = useId();
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose, open]);

  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div className="fixed inset-0 z-[100]" role="presentation">
      <button type="button" aria-label="Close drawer" className="absolute inset-0 bg-surface-canvas/80 backdrop-blur-sm" onClick={onClose} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={cn(
          "absolute inset-y-0 flex w-[min(100%,32rem)] flex-col border-card-border bg-surface-elevated text-foreground shadow-overlay",
          side === "right" ? "right-0 border-l" : "left-0 border-r",
          className,
        )}
      >
        <header className="flex items-start justify-between gap-ds-4 border-b border-card-border bg-surface-secondary px-ds-5 py-ds-4">
          <div><h2 id={titleId} className="text-title">{title}</h2>{description ? <p id={descriptionId} className="mt-ds-1 text-caption text-muted">{description}</p> : null}</div>
          <IconButton variant="ghost" aria-label="Close drawer" onClick={onClose}><X className="h-4 w-4" /></IconButton>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto p-ds-5">{children}</div>
        {footer ? <footer className="border-t border-card-border bg-surface-secondary p-ds-4">{footer}</footer> : null}
      </aside>
    </div>,
    document.body,
  );
}
